import { useCallback, useSyncExternalStore } from 'react';

// eslint-disable-next-line no-restricted-imports -- LocalStorageKey below gathers every key, this one included
import { JoiningChannelKey } from 'src/components/online-joining/redesign-feature-flag/membership-signup-form/utils/joining-channel-key';
import { SelectedAddOnsKey } from 'src/components/online-joining/redesign-feature-flag/membership-signup-form/utils/selected-add-ons-key';
// eslint-disable-next-line no-restricted-imports -- LocalStorageKey below gathers every key, this one included
import { SignupRefKey } from 'src/components/online-joining/redesign-feature-flag/membership-signup-form/utils/signup-ref-key';
import { DeepLinkRedirectStorageKey } from 'src/helpers/deep-link-redirect-storage-keys';
import { NewLogoStorageKey } from 'src/helpers/new-logo-storage-keys';
import { PurchaseStorageKey } from 'src/helpers/purchase-storage-keys';
import { RecentlyViewedClubStorageKey } from 'src/helpers/recently-viewed-club-storage-keys';

export const LocalStorageKeyGroups = {
  recentlyViewedClub: RecentlyViewedClubStorageKey,
  deepLinkRedirect: DeepLinkRedirectStorageKey,
  purchase: PurchaseStorageKey,
  signupRef: SignupRefKey,
  joiningChannel: JoiningChannelKey,
  selectedAddOns: SelectedAddOnsKey,
  newLogo: NewLogoStorageKey,
} as const;

type DeepValues<T> = T extends string ? T : { [K in keyof T]: DeepValues<T[K]> }[keyof T];

export type LocalStorageKey = DeepValues<typeof LocalStorageKeyGroups>;

const subscriptions = new Map<LocalStorageKey, Set<() => void>>();

const subscribe = (key: LocalStorageKey, onStoreChange: () => void) => {
  if (!subscriptions.has(key)) {
    subscriptions.set(key, new Set());
  }

  const keySubscriptions = subscriptions.get(key)!;
  keySubscriptions.add(onStoreChange);

  return () => {
    keySubscriptions.delete(onStoreChange);
  };
};

const notifyStoreChange = (key: LocalStorageKey) => {
  subscriptions.get(key)?.forEach((listener) => listener());
};

export const getLocalStorageItem = (item: LocalStorageKey): string | null => {
  if (typeof localStorage !== 'undefined') {
    return localStorage.getItem(item);
  }
  return null;
};

export const setLocalStorageItem = (item: LocalStorageKey, itemValue: string) => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(item, itemValue);
    notifyStoreChange(item);
  }
};

export const removeLocalStorageItem = (item: LocalStorageKey) => {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(item);
    notifyStoreChange(item);
  }
};

type UseLocalStorageResult = {
  value: string | null;
  setValue: (value: string) => void;
  clearValue: () => void;
};

export const useLocalStorage = (key: LocalStorageKey): UseLocalStorageResult => {
  const snapshot = useSyncExternalStore(
    (onStoreChange) => subscribe(key, onStoreChange),
    () => getLocalStorageItem(key),
    () => null
  );

  const setValue = useCallback(
    (value: string) => {
      setLocalStorageItem(key, value);
    },
    [key]
  );

  const clearValue = useCallback(() => {
    removeLocalStorageItem(key);
  }, [key]);

  return { value: snapshot, setValue, clearValue };
};
